import React, { useMemo, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Collapse,
    Descriptions,
    Empty,
    message,
    Space,
    Table,
    Tag,
    Typography,
    Upload,
} from "antd";
import {
    EyeOutlined,
    FileExcelOutlined,
    InboxOutlined,
    ReloadOutlined,
    UploadOutlined,
} from "@ant-design/icons";
import {
    previewTransactionScheduleExcel,
    uploadTransactionScheduleExcel,
} from "../api/transactionScheduleApi";

const { Dragger } = Upload;
const { Title, Text } = Typography;

function UploadTransactionScheduleExcel({
    onImportSuccess,
    extraAction,
    inModal = false,
}) {
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    const [previewErrors, setPreviewErrors] = useState([]);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [loadingImport, setLoadingImport] = useState(false);
    const [headersValid, setHeadersValid] = useState(false);
    const [importResult, setImportResult] = useState(null);

    const previewColumns = [
        { title: "Мөр", dataIndex: "rowNumber", key: "rowNumber", width: 80, fixed: "left" },
        { title: "Бүртгэлийн огноо, цаг", dataIndex: "registrationDateTime", key: "registrationDateTime", width: 180 },
        { title: "Хүсэлтийн дугаар", dataIndex: "requestNumber", key: "requestNumber", width: 160 },
        { title: "Хүсэлтийн огноо, цаг", dataIndex: "requestDateTime", key: "requestDateTime", width: 180 },
        { title: "RUB дүн", dataIndex: "amountRub", key: "amountRub", width: 140 },
        { title: "Төлбөрийн дүн", dataIndex: "paymentAmount", key: "paymentAmount", width: 140 },
        { title: "Валют", dataIndex: "currency", key: "currency", width: 100 },
        { title: "Төлөгч", dataIndex: "payer", key: "payer", width: 180 },
        { title: "Төлөгчийн банк", dataIndex: "payerBank", key: "payerBank", width: 220 },
        { title: "Хүлээн авагч улсын нэр", dataIndex: "beneficiaryCountry", key: "beneficiaryCountry", width: 180 },
        { title: "Хүлээн авагч байгууллага", dataIndex: "beneficiaryCompany", key: "beneficiaryCompany", width: 220 },
        { title: "Хүлээн авагчийн хаяг", dataIndex: "beneficiaryAddress", key: "beneficiaryAddress", width: 260 },
        { title: "Хүлээн авагч банк", dataIndex: "beneficiaryBank", key: "beneficiaryBank", width: 260 },
        { title: "Хүлээн авагчийн SWIFT", dataIndex: "beneficiarySwift", key: "beneficiarySwift", width: 180 },
        { title: "Хүлээн авагчийн данс", dataIndex: "beneficiaryAccount", key: "beneficiaryAccount", width: 220 },
        { title: "Гүйлгээний утга", dataIndex: "paymentPurpose", key: "paymentPurpose", width: 320 },
        { title: "Барааны ангилал", dataIndex: "goodsCategory", key: "goodsCategory", width: 180 },
        { title: "Тайлбар", dataIndex: "comments", key: "comments", width: 260 },
    ];

    const issueColumns = [
        {
            title: "Мөр",
            dataIndex: "rowNumber",
            key: "rowNumber",
            width: 100,
        },
        {
            title: "Хүсэлтийн дугаар",
            dataIndex: "requestNumber",
            key: "requestNumber",
            width: 180,
            render: (value) => value || "-",
        },
        {
            title: "Шалтгаан",
            dataIndex: "reason",
            key: "reason",
            render: (value) => value || "-",
        },
    ];

    const resetAll = () => {
        setSelectedFile(null);
        setPreviewData([]);
        setPreviewErrors([]);
        setHeadersValid(false);
        setImportResult(null);
    };

    const handlePreviewFile = async (file) => {
        setLoadingPreview(true);
        setPreviewErrors([]);
        setPreviewData([]);
        setHeadersValid(false);
        setImportResult(null);

        try {
            const data = await previewTransactionScheduleExcel(file);

            setHeadersValid(!!data.valid);
            setPreviewErrors(data.errors || []);
            setPreviewData(
                (data.rows || []).map((item, index) => ({
                    ...item,
                    key: `${item.rowNumber}-${index}`,
                })),
            );

            if (data.valid) {
                message.success("Excel урьдчилсан харагдац ачааллаа");
            } else {
                message.error("Excel файлын шалгалт амжилтгүй");
            }
        } catch (error) {
            message.error(error.message || "Урьдчилсан харагдац ачаалж чадсангүй");
        } finally {
            setLoadingPreview(false);
        }
    };

    const uploadProps = {
        multiple: false,
        accept: ".xlsx,.xls",
        beforeUpload: (file) => {
            setSelectedFile(file);
            handlePreviewFile(file);
            return false;
        },
        onRemove: () => {
            resetAll();
        },
    };

    const handleImport = async () => {
        if (!selectedFile) {
            message.error("Excel файл сонгоно уу");
            return;
        }

        if (!headersValid) {
            message.error("Excel файлын бүтэц буруу байна");
            return;
        }

        setLoadingImport(true);

        try {
            const data = await uploadTransactionScheduleExcel(selectedFile);
            setImportResult(data || null);

            if (data.success) {
                const saved = data.savedCount || 0;
                const skipped = data.skippedCount || 0;
                const failed = data.failedCount || 0;

                message.success(
                    data.message ||
                    `Импорт дууслаа. Хадгалсан: ${saved}, Алгассан: ${skipped}, Алдаатай: ${failed}`,
                );

                if (onImportSuccess && saved > 0) {
                    await onImportSuccess();
                }
            } else {
                setPreviewErrors(data.errors || []);
                message.error(data.message || "Импорт амжилтгүй");
            }
        } catch (error) {
            message.error(error.message || "Импорт амжилтгүй");
        } finally {
            setLoadingImport(false);
        }
    };

    const statusTag = useMemo(() => {
        if (loadingPreview) {
            return <Tag color="processing">Урьдчилан шалгаж байна...</Tag>;
        }

        if (!selectedFile) {
            return <Tag>Файл сонгоогүй</Tag>;
        }

        return headersValid ? (
            <Tag color="green">Хүчинтэй Excel</Tag>
        ) : (
            <Tag color="red">Хүчингүй Excel</Tag>
        );
    }, [headersValid, loadingPreview, selectedFile]);

    const summaryBlock = (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: inModal
                    ? "repeat(auto-fit, minmax(180px, 1fr))"
                    : "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
            }}
        >
            <Card size="small">
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>Файл</div>
                <div style={{ fontWeight: 600, marginTop: 4 }}>
                    {selectedFile?.name || "-"}
                </div>
            </Card>

            <Card size="small">
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>Урьдчилсан мөр</div>
                <div style={{ fontWeight: 600, marginTop: 4 }}>
                    {previewData.length}
                </div>
            </Card>

            <Card size="small">
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>Алдаа</div>
                <div style={{ fontWeight: 600, marginTop: 4 }}>
                    {previewErrors.length}
                </div>
            </Card>

            <Card size="small">
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>Төлөв</div>
                <div style={{ marginTop: 6 }}>{statusTag}</div>
            </Card>
        </div>
    );

    const importResultBlock = importResult?.success ? (
        <Card
            size="small"
            title="Импортын дүн"
            style={{ background: "#fafafa" }}
        >
            <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 4 }}>
                <Descriptions.Item label="Нийт мөр">
                    {importResult.totalRows || 0}
                </Descriptions.Item>
                <Descriptions.Item label="Хадгалсан">
                    <Tag color="green">{importResult.savedCount || 0}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Алгассан">
                    <Tag color="gold">{importResult.skippedCount || 0}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Алдаатай">
                    <Tag color="red">{importResult.failedCount || 0}</Tag>
                </Descriptions.Item>
            </Descriptions>

            <div style={{ marginTop: 12 }}>
                <Text>{importResult.message}</Text>
            </div>

            {((importResult.skippedRows && importResult.skippedRows.length > 0) ||
                (importResult.failedRows && importResult.failedRows.length > 0)) && (
                    <Collapse
                        style={{ marginTop: 16 }}
                        items={[
                            ...(importResult.skippedRows?.length > 0
                                ? [
                                    {
                                        key: "skipped",
                                        label: `Алгассан мөрүүд (${importResult.skippedRows.length})`,
                                        children: (
                                            <Table
                                                rowKey={(row, index) =>
                                                    `${row.rowNumber}-${index}-skipped`
                                                }
                                                columns={issueColumns}
                                                dataSource={importResult.skippedRows}
                                                pagination={{ pageSize: 5 }}
                                                scroll={{ x: 700 }}
                                                size="small"
                                                bordered
                                            />
                                        ),
                                    },
                                ]
                                : []),
                            ...(importResult.failedRows?.length > 0
                                ? [
                                    {
                                        key: "failed",
                                        label: `Алдаатай мөрүүд (${importResult.failedRows.length})`,
                                        children: (
                                            <Table
                                                rowKey={(row, index) =>
                                                    `${row.rowNumber}-${index}-failed`
                                                }
                                                columns={issueColumns}
                                                dataSource={importResult.failedRows}
                                                pagination={{ pageSize: 5 }}
                                                scroll={{ x: 700 }}
                                                size="small"
                                                bordered
                                            />
                                        ),
                                    },
                                ]
                                : []),
                        ]}
                    />
                )}
        </Card>
    ) : null;

    const previewBlock =
        previewData.length > 0 ? (
            <Collapse
                defaultActiveKey={inModal ? [] : ["preview"]}
                items={[
                    {
                        key: "preview",
                        label: (
                            <Space size={8}>
                                <EyeOutlined />
                                <span>Урьдчилан харах мөрүүд ({previewData.length})</span>
                            </Space>
                        ),
                        children: (
                            <Table
                                columns={previewColumns}
                                dataSource={previewData}
                                pagination={{
                                    defaultPageSize: inModal ? 5 : 10,
                                    pageSizeOptions: [5, 10, 20, 50],
                                    showSizeChanger: true,
                                }}
                                scroll={{ x: 3500, y: inModal ? 360 : undefined }}
                                bordered
                                size={inModal ? "small" : "middle"}
                            />
                        ),
                    },
                ]}
            />
        ) : (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Урьдчилсан мөр байхгүй"
            />
        );

    const content = (
        <div className="w-full flex flex-col gap-4">
            <div
                style={{
                    display: "flex",
                    alignItems: inModal ? "flex-start" : "center",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                }}
            >
                <div>
                    {!inModal ? (
                        <Title level={4} className="!mb-0">
                            Excel оруулах
                        </Title>
                    ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <FileExcelOutlined style={{ fontSize: 18 }} />
                            <Text strong style={{ fontSize: 16 }}>
                                Excel импорт
                            </Text>
                        </div>
                    )}

                    <div style={{ marginTop: 4 }}>
                        <Text type="secondary">
                            Excel файлаа сонгоод урьдчилан шалгаж, дараа нь импортлоно.
                        </Text>
                    </div>
                </div>

                <Space wrap>
                    {extraAction}
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={resetAll}
                        disabled={!selectedFile && previewData.length === 0 && previewErrors.length === 0 && !importResult}
                    >
                        Цэвэрлэх
                    </Button>
                    <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={handleImport}
                        loading={loadingImport}
                        disabled={!headersValid || previewData.length === 0}
                    >
                        Импортлох
                    </Button>
                </Space>
            </div>

            <Dragger
                {...uploadProps}
                fileList={selectedFile ? [selectedFile] : []}
                style={{
                    padding: inModal ? "8px 0" : undefined,
                }}
            >
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                <p className="ant-upload-text">Excel файлыг дарж эсвэл чирж оруулна уу</p>
                <p className="ant-upload-hint">
                    Урьдчилсан харагдац автоматаар эхэлнэ. .xls болон .xlsx дэмжинэ
                </p>
            </Dragger>

            {summaryBlock}

            {previewErrors.length > 0 && (
                <Alert
                    type="error"
                    message="Excel шалгалтын алдаа"
                    description={
                        <ul className="list-disc pl-5">
                            {previewErrors.map((err, idx) => (
                                <li key={idx}>{err}</li>
                            ))}
                        </ul>
                    }
                />
            )}

            {importResultBlock}

            {previewBlock}
        </div>
    );

    if (inModal) {
        return content;
    }

    return <Card>{content}</Card>;
}

export default UploadTransactionScheduleExcel;